import { useCallback, useState } from "react";

type Vector = {
  x: number;
  y: number;
};
type DragState = {
  start: Vector;
  current: Vector;
  delta: Vector;
};
export type DragStartEvent = {
  type: "dragstart";
  event: React.MouseEvent;
} & DragState;
export type DragMoveEvent = {
  type: "dragmove" | "dragend";
  event: MouseEvent;
} & DragState;

type UseDraggableProps = {
  /**
   * This should return a function that will be called when the mouse is moved.
   * If the return value is undefined, following mouse events will be ignored.
   */
  onDragStart: (
    event: DragStartEvent
  ) => ((event: DragMoveEvent) => void) | undefined;
};
export function useDraggable({ onDragStart }: UseDraggableProps) {
  const [isDragging, setIsDragging] = useState(false);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      const start = { x: e.clientX, y: e.clientY };
      const onDrag = onDragStart({
        type: "dragstart",
        event: e,
        ...toDragState(e.nativeEvent, start),
      });

      if (!onDrag) return;
      setIsDragging(true);

      const handleMouseMove = (event: MouseEvent) => {
        onDrag({
          type: "dragmove",
          event,
          ...toDragState(event, start),
        });
      };
      const handleMouseUp = (event: MouseEvent) => {
        setIsDragging(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        onDrag({
          type: "dragend",
          event,
          ...toDragState(event, start),
        });
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [onDragStart]
  );

  return { isDragging, onMouseDown };
}

function toDragState(event: MouseEvent, start: Vector): DragState {
  return {
    start: { ...start },
    current: { x: event.clientX, y: event.clientY },
    delta: { x: event.clientX - start.x, y: event.clientY - start.y },
  };
}
